import Stripe from "stripe";

import {
  FilterParams,
  PaginatedParams,
  PaginatedData
} from "src/library/utils";
import {
  IDrop,
  ListDropsByCustomerParams
} from "src/core/drops/api/drops.api";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import { ITransaction } from "src/modules/payment/api/transactions.api";
import { IUserBranch } from "src/core/users/api/users.api";

import { ListTransactionsParams } from "./transactions.api";

type PaymentEndpointConfig = {
  CREATE_STRIPE_SETUP_INTENT: EndpointConfig<
    CreatePaymentIntentParams,
    Stripe.Response<Stripe.SetupIntent>
  >;
  REFUND_ORDER: EndpointConfig<
    { refundAmount: number; orderId: number },
    { message: string }
  >;
  LIST_PAYMENT_METHODS: EndpointConfig<
    ListPaymentCardsParams,
    IPaymentCard[]
  >;
  CREATE_PAYMENT_METHOD: EndpointConfig<
    CreatePaymentMethodParams,
    IPaymentCard
  >;
  GET_PAYMENT_METHOD: EndpointConfig<
    number,
    IPaymentCard
  >;
  DELETE_PAYMENT_METHOD: EndpointConfig<
    number,
    IPaymentCard
  >;
  GET_PAYMENT_METHOD_TRANSACTIONS: EndpointConfig<
    ListPaymentCardTransactionParams,
    PaginatedData<ITransaction>
  >;
  UPDATE_USER_BRANCHES_DEFAULT_PAYMENT: EndpointConfig<
    UpdateUserBranchesDefaultPaymentParams,
    IPaymentCard
  >;
  ASSIGN_BRANCHES_TO_PAYMENT_METHOD: EndpointConfig<
    AssignBranchesToPaymentCardParams,
    IPaymentCardBranch[]
  >;
  DELETE_PAYMENT_CARD_BRANCH: EndpointConfig<
    DeletePaymentCardBranchParams,
    IPaymentCardBranch
  >;
  GET_PAYMENT_METHOD_BRANCHES: EndpointConfig<
    ListPaymentCardBranchParams,
    PaginatedData<IDrop>
  >;
  CREATE_ORDER_TRANSACTION: EndpointConfig<
    CreateOrderTransaction,
    ITransaction
  >;
  RESYNC_LATEST_ORDER_TRANSACTION: EndpointConfig<
    number,
    ITransaction
  >;
  GENERATE_FINANCIAL_REPORT: EndpointConfig<
    { fromDate: Date, toDate: Date },
    { message: string }
  >;
};

/**
 * Payment API
 */
const requests: RequestConfig<PaymentEndpointConfig> = {
  CREATE_STRIPE_SETUP_INTENT: data => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/stripe/setup-intent`,
      method: "POST",
      data
    };
  },

  REFUND_ORDER: ({ orderId, ...data }) => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/orders/${orderId}/refund`,
      method: "POST",
      data
    };
  },

  LIST_PAYMENT_METHODS: params => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/methods`,
      method: "GET",
      params
    };
  },
  CREATE_PAYMENT_METHOD: data => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/methods/`,
      method: "POST",
      data
    };
  },
  GET_PAYMENT_METHOD: paymentMethodId => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/methods/${paymentMethodId}`,
      method: "GET"
    };
  },
  DELETE_PAYMENT_METHOD: paymentMethodId => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/methods/${paymentMethodId}`,
      method: "DELETE"
    };
  },

  GET_PAYMENT_METHOD_TRANSACTIONS:
      ({ paymentMethodId, params }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/payment/methods/${paymentMethodId}/transactions`,
          method: "GET",
          params
        };
      },

  UPDATE_USER_BRANCHES_DEFAULT_PAYMENT: ({ paymentCardId }) => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/methods/${paymentCardId}/default-payment`,
      method: "PATCH",
      data: { paymentCardId }
    };
  },

  ASSIGN_BRANCHES_TO_PAYMENT_METHOD: ({ paymentMethodId, branchIds }) => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/methods/${paymentMethodId}/branches`,
      method: "POST",
      data: { branchIds }
    };
  },

  DELETE_PAYMENT_CARD_BRANCH:
      ({ paymentMethodId, branchId }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/payment/methods/${paymentMethodId}/branches/${branchId}`,
          method: "DELETE"
        };
      },

  GET_PAYMENT_METHOD_BRANCHES: ({ paymentMethodId, params }) => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/methods/${paymentMethodId}/branches`,
      method: "GET",
      params
    };
  },
  CREATE_ORDER_TRANSACTION: ({ orderId, paymentCardId }) => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/orders/${orderId}/transactions`,
      method: "POST",
      data: { paymentCardId }
    };
  },
  RESYNC_LATEST_ORDER_TRANSACTION: orderId => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/orders/${orderId}/transactions`,
      method: "PATCH"
    };
  },
  GENERATE_FINANCIAL_REPORT: data => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/stripe/generate-financial-report`,
      method: "POST",
      data
    };
  }
};

export const usePaymentApi: UseApiRequest<PaymentEndpointConfig> = name => useAxios(requests[ name ], name);

export type CreatePaymentMethodParams = { externalId: string; defaultCard: boolean; };

export type CreatePaymentIntentParams = { sessionType: Stripe.SetupIntentCreateParams.Usage, paymentCardId?: number; };
export type CreateOrderTransaction = { orderId: number; paymentCardId?: number;};

export type PartialPaymentCard = Partial<IPaymentCard>;
export type ListPaymentCardsParams<T extends PartialPaymentCard = IPaymentCard> = {
  pagination?: PaginatedParams<T>;
  filters?: FilterParams<IPaymentCard>;
};

export type ListPaymentCardTransactionParams = { paymentMethodId: number, params: ListTransactionsParams };
export type ListPaymentCardBranchParams = { paymentMethodId: number, params: ListDropsByCustomerParams };
export type DeletePaymentCardBranchParams = { paymentMethodId: number, branchId: number };
export type AssignBranchesToPaymentCardParams = { paymentMethodId: number, branchIds: number[] };
export type UpdateUserBranchesDefaultPaymentParams = { paymentCardId: number };

export interface IPaymentCard {
  id: number;
  brand: PaymentCardBrand;
  funding: PaymentCardFunding;
  expiryMonth: number;
  expiryYear: number;
  last4: string
  usage: PaymentCardUsage,
  status: PaymentCardStatus;
  createdAt: Date;
  updatedAt: Date;
  lastUsed?: Date;
  branchPaymentCards?: IUserBranch[];
}

export enum PaymentCardBrand {
  "VISA" = "visa",
  "MASTERCARD" = "mastercard",
  "AMEX" = "american_express",
  "DISCOVER" = "discover",
  "DC" = "diners_club",
  "UNION" = "union_pay",
  "JCB" = "jcb",
  "CB" = "cartes_bancaires",
  "UNKNOWN" = "unknown"
}

export enum PaymentCardFunding {
  "CREDIT" = "credit",
  "DEBIT" = "debit",
  "PREPAID" = "prepaid"
}

export enum PaymentCardUsage {
  "OFF_SESSION" = "off_session",
  "ON_SESSION" = "on_session"
}

export enum PaymentCardStatus {
  "ACTIVE" = "active",
  "EXPIRED" = "expired",
  "DELETED" = "deleted"
}

export interface IPaymentCardBranch {
  id: number;
  paymentCardId: IPaymentCard["id"];
  branchId: IDrop["id"];
}
